import * as React from 'react';
import VideoViewBackground from '~/components/ViewBackground/VideoViewBackground';
import GalleryViewBackground from '~/components/ViewBackground/GalleryViewBackground';
import { isDisabledCategoryVideoForBundlePage } from '~/utils/bundles';
import { isEmptyObject, isMobileOrTabletWindow } from '~/utils/utils';
import styles from './ViewBackground.scss';
import classNames from 'classnames';
import { getGlobalContent, settings } from '~/utils/settings';
import GroupingPageBackground from '~/Layouts/Themes/GroupingPage/GroupingPageBackground';
import AuctionBackground from '~/Layouts/AuctionLotPage/AuctionBackground';
import SnowfallBackground from '~/Layouts/Themes/Snowfall/SnowfallBackground';
import RefManager, { RefManagerKeys } from '~/RefManager';
import Anniversary from '~/Layouts/Themes/Anniversary/Anniversary';
import equal from 'fast-deep-equal/react';
import { BUNDLE_DECORATION } from '~/types/bundle';
import isDisabledAnimation from '~/hooks/isDisabledAnimation';
import { useAvailableSound } from '~/hooks/useAvailableSound';
import { shipyardSoundOff, shipyardSoundOn } from '@wg/web2clientapi/sound';
import { RootState, useAppSelector } from '~/Store';
import { CategoryThemes } from '~/types/category';

interface IThemeBackground {
    theme: CategoryThemes;
}

const themeComponents: Partial<Record<CategoryThemes, React.FunctionComponent>> = {
    [CategoryThemes.AUCTION_PAGE_THEME]: AuctionBackground,
    [CategoryThemes.ANNIVERSARY_PROMO_THEME]: Anniversary,
    [CategoryThemes.SNOWFALL]: SnowfallBackground,
};

const ThemeBackground = React.memo(({ theme }: IThemeBackground) => {
    const ThemeComponent = themeComponents[theme];
    return ThemeComponent ? <ThemeComponent /> : null;
});

interface IViewBackground {
    isBlur?: boolean;
}

const stateSelector = (state: RootState) => {
    return {
        currentPage: state.app.currentPage,
        viewBackground: state.app.viewBackground,
        viewBackgroundColor: state.app.viewBackgroundColor,
        categories: state.app.categories,
        menu: state.app.menu,
        bundles: state.app.bundles,
        isFullscreen: state.app.isFullscreen,
        isTrusted: state.app.isTrusted,
    };
};

const ViewBackground = (props: IViewBackground): React.ReactElement => {
    const state = useAppSelector(stateSelector, equal);
    const category = state.categories?.[state.currentPage?.name];
    const bundle = state.bundles?.[state.currentPage?.bundleId];
    const configCategory = state.menu?.[state.currentPage?.name];

    let bundleVideoBackground = bundle?.videoBackground;
    let hasBundleVideoBackground = !isEmptyObject(bundleVideoBackground);
    let animationName = hasBundleVideoBackground ? bundle.name : category?.name;

    if (!hasBundleVideoBackground && bundle?.parentBundleId) {
        const { name, videoBackground } = state.bundles[bundle.parentBundleId];
        bundleVideoBackground = videoBackground;
        hasBundleVideoBackground = !isEmptyObject(bundleVideoBackground);
        if (hasBundleVideoBackground) {
            animationName = name;
        }
    }

    const _isDisabledAnimation = isDisabledAnimation(animationName);
    const isAvailableSoundEffect = useAvailableSound(category, bundle);

    React.useEffect(() => {
        if (state.isTrusted && !_isDisabledAnimation) {
            isAvailableSoundEffect ? shipyardSoundOff() : shipyardSoundOn();
        }
    }, [isAvailableSoundEffect, _isDisabledAnimation, state.isTrusted]);

    const setRef = (ref: HTMLDivElement) => {
        if (ref) {
            RefManager.setRef(RefManagerKeys.ViewBackground, ref);
        }
    };

    const setPortalRef = (ref: HTMLDivElement) => {
        if (ref) {
            RefManager.setRef(RefManagerKeys.ViewBackgroundPortal, ref);
        }
    };

    const theme = category?.theme || configCategory?.theme || getGlobalContent()?.theme;
    if (theme === CategoryThemes.GROUPING_PAGE_THEME && !state.currentPage?.isBundlePage) {
        return <GroupingPageBackground key={`category_${category.name}`} />;
    }

    const hasCategoryVideoBackground = !isEmptyObject(category?.videoBackground);
    let hasVideoBackground = false;
    if (!isMobileOrTabletWindow) {
        if (bundle) {
            hasVideoBackground = hasBundleVideoBackground;
            const disableCategoryVideoBack = bundle.decoration?.includes(BUNDLE_DECORATION.DISABLE_CATEGORY_VIDEO_BACK);
            if (!bundle.serialPurchase && !disableCategoryVideoBack && !hasVideoBackground && !isDisabledCategoryVideoForBundlePage(bundle)) {
                hasVideoBackground = hasCategoryVideoBackground;
            }
        } else {
            hasVideoBackground = hasCategoryVideoBackground;
        }
    }

    // Fullscreen gallery [images or video slider]
    if (!isMobileOrTabletWindow && bundle?.isFullscreenGallery) {
        return <GalleryViewBackground isBlur={props.isBlur} bundle={bundle} category={category} />;
    }

    if (!hasVideoBackground || _isDisabledAnimation) {
        const backgroundColor = bundle?.backgroundColor || category?.backgroundColor || state.viewBackgroundColor;
        const hasBackgroundMask = !!backgroundColor;
        const mergedStyles: React.CSSProperties = {};
        if (state.viewBackground) {
            mergedStyles.backgroundImage = `url(${state.viewBackground})`;
        }

        const style = hasBackgroundMask ? ({ '--bundle-background-color': backgroundColor, ...mergedStyles } as React.CSSProperties) : mergedStyles;

        return (
            <div
                ref={setRef}
                style={style}
                key={`background_${category?.name}`}
                className={classNames(
                    styles.background,
                    state.currentPage?.name,
                    styles[state.currentPage?.name],
                    styles[configCategory?.backgroundName],
                    styles[settings.realm],
                    styles[configCategory?.theme],
                    {
                        [styles.blur]: props.isBlur,
                        [styles.isBundlePage]: state.currentPage?.isBundlePage,
                        [styles.backgroundWithMask]: hasBackgroundMask,
                        [styles.isFullscreen]: state.isFullscreen,
                    },
                )}
            >
                <div className={classNames(styles.backgroundMask, { [styles.visibleMask]: hasBackgroundMask })} />
                <ThemeBackground theme={theme} key={category?.name} />
                <div ref={setPortalRef} />
            </div>
        );
    }

    const videoBackgroundKey = hasBundleVideoBackground ? `background_${category?.name}_${bundle.id}` : `background_${category?.videoBackground?.webm}`;

    return (
        <VideoViewBackground isBlur={props.isBlur} key={videoBackgroundKey} bundle={bundle} category={category}>
            <ThemeBackground theme={theme} key={category?.name} />
            <div ref={setPortalRef} />
        </VideoViewBackground>
    );
};

export default ViewBackground;
